import React from 'react';
import { FormData } from '../types';
import { SelectField, TextField } from '../../../components/ui/Field';
import { formatDuration, openMinutes } from '../../../lib/formRules';

interface EventDetailsSectionProps {
  formData: FormData;
  updateFormData: (field: keyof FormData, value: FormData[keyof FormData]) => void;
  validationErrors?: { [key: string]: string };
  minimumPersons: number;
}

// Half-hour slots from 10:00 until 02:00 the next night
const TIME_OPTIONS = Array.from({ length: 33 }, (_, index) => {
  const minutes = 10 * 60 + index * 30;
  const hour = Math.floor(minutes / 60) % 24;
  return `${String(hour).padStart(2, '0')}:${minutes % 60 === 0 ? '00' : '30'}`;
});

const todayIso = () => new Date().toISOString().slice(0, 10);

export const EventDetailsSection: React.FC<EventDetailsSectionProps> = ({
  formData,
  updateFormData,
  validationErrors = {},
  minimumPersons,
}) => {
  const setCount = (field: 'volwassenen' | 'kinderen' | 'vegetarisch', value: string) => {
    const parsed = parseInt(value, 10);
    updateFormData(field, Number.isNaN(parsed) ? 0 : Math.max(0, parsed));
  };
  const totalGuests = (formData.volwassenen || 0) + (formData.kinderen || 0);
  const minutes = openMinutes(formData.startTijd, formData.eindTijd);

  return (
    <div className="space-y-8">
      <fieldset>
        <legend className="font-display text-lg font-bold">Hoeveel gasten verwacht je?</legend>
        <p className="mt-1 text-sm text-bl-black/60">We komen vanaf {minimumPersons} gasten. Kinderen tellen mee.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <TextField
            label="Volwassenen"
            name="volwassenen"
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="bv. 40"
            value={formData.volwassenen || ''}
            onValueChange={(value) => setCount('volwassenen', value)}
            error={validationErrors.volwassenen}
          />
          <TextField
            label="Kinderen"
            name="kinderen"
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="0"
            optional
            value={formData.kinderen || ''}
            onValueChange={(value) => setCount('kinderen', value)}
            error={validationErrors.kinderen}
          />
          <TextField
            label="Waarvan vegetarisch"
            hint="Tel je mee bij de volwassenen."
            name="vegetarisch"
            type="number"
            inputMode="numeric"
            min={0}
            placeholder="0"
            optional
            value={formData.vegetarisch || ''}
            onValueChange={(value) => setCount('vegetarisch', value)}
          />
        </div>
        {totalGuests > 0 && !validationErrors.volwassenen && (
          <p className="mt-2 text-sm text-bl-brown">{totalGuests} gasten in totaal</p>
        )}
      </fieldset>

      <fieldset>
        <legend className="font-display text-lg font-bold">Wanneer is je feest?</legend>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <TextField
            label="Datum"
            name="datum"
            type="date"
            min={todayIso()}
            value={formData.datum}
            onValueChange={(value) => updateFormData('datum', value)}
            error={validationErrors.datum}
          />
          <SelectField
            label="Foodtruck open vanaf"
            name="startTijd"
            value={formData.startTijd}
            onValueChange={(value) => updateFormData('startTijd', value)}
            error={validationErrors.startTijd}
          >
            <option value="">Kies een uur</option>
            {TIME_OPTIONS.map((time) => (
              <option key={time} value={time}>{time}</option>
            ))}
          </SelectField>
          <SelectField
            label="Tot"
            name="eindTijd"
            value={formData.eindTijd}
            onValueChange={(value) => updateFormData('eindTijd', value)}
            error={validationErrors.eindTijd}
          >
            <option value="">Kies een uur</option>
            {TIME_OPTIONS.map((time) => (
              <option key={time} value={time}>{time}</option>
            ))}
          </SelectField>
        </div>
        {minutes !== null && !validationErrors.eindTijd && (
          <p className="mt-2 text-sm text-bl-brown">De foodtruck is {formatDuration(minutes)} open.</p>
        )}
      </fieldset>

      <fieldset>
        <legend className="font-display text-lg font-bold">Zijn er nog andere foodtrucks of cateraars?</legend>
        <div className="mt-3 flex gap-3">
          {[
            { label: 'Ja', value: true },
            { label: 'Nee', value: false },
          ].map((option) => {
            const checked = formData.andereFoodtrucks === option.value;
            return (
              <label
                key={option.label}
                className={`cursor-pointer rounded border-2 px-6 py-2.5 text-sm font-bold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-bl-gold ${
                  checked ? 'border-bl-brown bg-bl-brown text-white' : 'border-bl-black/15 hover:border-bl-brown'
                }`}
              >
                <input
                  type="radio"
                  name="andereFoodtrucks"
                  className="sr-only"
                  checked={checked}
                  onChange={() => updateFormData('andereFoodtrucks', option.value)}
                />
                {option.label}
              </label>
            );
          })}
        </div>
        {validationErrors.andereFoodtrucks && <p className="field-message">{validationErrors.andereFoodtrucks}</p>}
      </fieldset>
    </div>
  );
};
